import {
	DateString,
	FormattedText,
	ScrollableVirtualList,
	PipeSeparator
} from "client/Components";
import { SelectableItemOptions, useUserTimezone } from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { ReactElement, useCallback } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DocumentListItem } from "../../DocumentListItem";
import {
	GridItem,
	RecycleBinAction,
	RecycleBinActionContext
} from "./SharePointRecycleBin";
import { TimeZone } from "client/DateAndTime/timezones";
import { DocumentNoResult } from "../../DocumentNoResult";

export type SharePointRecycleBinListViewProps = {
	items: readonly GridItem[];
	selectionFor: (item: GridItem) => SelectableItemOptions;
	selectionActions: readonly RecycleBinAction[];
	actionContext: RecycleBinActionContext;
};

export const SharePointRecycleBinListView = (props: SharePointRecycleBinListViewProps): ReactElement => {
	const {
		items,
		selectionFor,
		selectionActions,
		actionContext
	} = props;

	const userTimezone: TimeZone = useUserTimezone();

	const noopHandler = useCallback(
		() => {
			// Design wants a different UX flow for "opening" recyclebin items, this will be implemented
			// in a future PBI. For now we are always in "edit mode" until this new UX is implemented
		},
		[]
	);

	if (items.length === 0) {
		return (
			<DocumentNoResult
				hasSearch={false}
			/>
		);
	}

	return (
		<ScrollableVirtualList
			items={items}
			estimatedHeightOfAnItem={64}
			itemKey={getUniqueKey}
			renderAfterItems={
				<ScrollableVirtualList.MobilePlusMenuSpacing />
			}
			trackingId="sharepoint-recycle-bin-list-view"
		>
			{(x: GridItem): ReactElement => (
				<DocumentListItem
					name={x.name}
					additionalInformation={<AdditionalInformation item={x} timezone={userTimezone} />}
					documentKind={x.model.itemType}
					extension={x.extension}
					isInSelectMode
					onEnableSelectMode={noopHandler}
					selectable={selectionFor(x)}
					onOpenItem={noopHandler}
					actions={selectionActions}
					actionContext={{ ...actionContext, selection: [x.model] }}
				/>
			)}
		</ScrollableVirtualList>
	);
};
SharePointRecycleBinListView.displayName = "SharePointRecycleBinListView";

function getUniqueKey(item: GridItem): string {
	return item.id;
}

type AdditionalInformationProps = {
	item: GridItem;
	timezone: TimeZone;
};

const AdditionalInformation = (props: AdditionalInformationProps): ReactElement => {
	const {
		item,
		timezone
	} = props;

	return (
		<>
			<FormattedText
				text={localize("_DeletedDate")}
				thingsToReplace={[
					{
						what: "{{Date}}",
						filler: (
							<DateString
								value={item.deletedOn}
								format="RelativeTimestamp"
								timezone={timezone}
							/>
						)
					}
				]}
			/>
			{isNotNil(item.size) && (
				<>
					<PipeSeparator />
					{getDisplayFriendlySize(item.size)}
				</>
			)}

		</>
	);
};
AdditionalInformation.displayName = "AdditionalInformation";
